import React, { useEffect, useState } from "react";
import TimeInput from "../../../../../components/TimeInput";
import IconButton from "../../../../../components/IconButton";
import SaveDot, { SaveState } from "../../../../../components/SaveDot";
import { CloseIcon, ResetIcon } from "../../../../../components/IconButton/icons";
import {
  displayTime,
  formatTime,
  LunchBreak,
  minutesToTime,
  timeToMinutes,
} from "../../../../../utils/timesheetTime";
import { LunchClash } from "../../../timesheetDerive";

/** team_members_lunch_minutes_check / timesheet_day_lunches_lunch_minutes_check. */
const MAX_LUNCH_MINUTES = 240;

export const LUNCH_FIELDS = ["start", "end", "reset", "none"] as const;
export type LunchField = (typeof LUNCH_FIELDS)[number];

export interface DayLunchRowProps {
  /** The lunch that applies on this day. */
  lunch: LunchBreak;
  /** The employee's usual lunch, for the hint and for adding one back. */
  usual: LunchBreak;
  /** True when the day has its own lunch rather than the usual one. */
  isCustom: boolean;
  /** An entry that runs into the break from one side, if any. */
  clash: LunchClash | null;
  isReadOnly: boolean;
  columns: number;
  saveState: (field: LunchField) => SaveState;
  /** null goes back to the usual lunch. */
  onChange: (field: LunchField, next: LunchBreak | null) => void;
}

const range = (lunch: LunchBreak): string =>
  `${displayTime(lunch.start)}–${displayTime(minutesToTime(timeToMinutes(formatTime(lunch.start)) + lunch.minutes))}`;

/**
 * The day's lunch break as a row of the timeline, placed by dayRows() either
 * in the gap it was taken in or straight after the entry it falls inside.
 * Its start and end are edited like an entry's: a new start keeps the end
 * (or, past it, keeps the length); a new end sets the length. Clearing either
 * means no lunch that day. Every change re-derives the day's timed hours on
 * the server, so the entries around it move with it.
 */
const DayLunchRow: React.FC<DayLunchRowProps> = ({
  lunch,
  usual,
  isCustom,
  clash,
  isReadOnly,
  columns,
  saveState,
  onChange,
}) => {
  const taken = lunch.minutes > 0;
  const start = formatTime(lunch.start);
  const end = taken
    ? minutesToTime(timeToMinutes(start) + lunch.minutes)
    : null;

  // Same refusal model as DayEntryRow: said under the row, field remounted.
  const [refusal, setRefusal] = useState<string | null>(null);
  const [restoreKey, setRestoreKey] = useState(0);
  const refuse = (message: string) => {
    setRefusal(message);
    setRestoreKey((k) => k + 1);
  };
  const commit = (field: LunchField, next: LunchBreak) => {
    if (next.minutes > MAX_LUNCH_MINUTES) {
      refuse("A lunch break can be at most 4 hours.");
      return;
    }
    if (timeToMinutes(next.start) + next.minutes > 24 * 60) {
      refuse("The lunch break must end by midnight.");
      return;
    }
    setRefusal(null);
    onChange(field, next);
  };

  const commitStart = (next: string | null) => {
    if (next === null) {
      commit("start", { start, minutes: 0 });
      return;
    }
    if (!end) {
      commit("start", { start: next, minutes: usual.minutes || 60 });
      return;
    }
    const minutes = timeToMinutes(end) - timeToMinutes(next);
    // Past the end: keep the length and move the whole break.
    commit("start", { start: next, minutes: minutes > 0 ? minutes : lunch.minutes });
  };

  const commitEnd = (next: string | null) => {
    if (next === null) {
      commit("end", { start, minutes: 0 });
      return;
    }
    const minutes = timeToMinutes(next) - timeToMinutes(start);
    if (minutes <= 0) {
      refuse(`Lunch must end after its ${displayTime(start)} start.`);
      return;
    }
    commit("end", { start, minutes });
  };

  const states: SaveState[] = LUNCH_FIELDS.map(saveState);

  // A change the server refused (one that would swallow a whole entry, a
  // locked week) leaves the lunch as it was: remount the inputs so they show
  // the saved times again rather than the refused ones. The toast says why
  // and the row's dot stays red; the boxes are not outlined, because what
  // they now hold is the saved lunch, not the refused one.
  const failed = states.includes("error");
  useEffect(() => {
    if (failed) setRestoreKey((k) => k + 1);
  }, [failed]);
  const rowState: SaveState = states.includes("error")
    ? "error"
    : states.includes("saving")
      ? "saving"
      : states.includes("saved")
        ? "saved"
        : "idle";

  const usualText = usual.minutes > 0 ? `usually ${range(usual)}` : "usually none";

  return (
    <>
      <tr className={`day-view__lunch ${taken ? "" : "is-none"} ${clash ? "is-clash" : ""}`}>
        <td className="day-view__dot-col">
          {rowState !== "idle" && <SaveDot state={rowState} />}
        </td>
        <td className="day-view__time-col">
          {isReadOnly ? (
            <span className="ui-table__mono">{taken ? displayTime(start) : "–"}</span>
          ) : (
            <TimeInput
              key={restoreKey}
              value={taken ? start : null}
              onCommit={commitStart}
              aria-label="Lunch start"
            />
          )}
        </td>
        <td className="day-view__time-col">
          {isReadOnly ? (
            <span className="ui-table__mono">{end ? displayTime(end) : "–"}</span>
          ) : (
            <TimeInput
              key={restoreKey}
              value={end}
              onCommit={commitEnd}
              aria-label="Lunch end"
            />
          )}
        </td>
        <td className="day-view__hours-col">
          <span className="day-view__lunch-length">
            {/* h:mm, not "1 h 30 min": it has to fit the hours column. */}
            {taken ? `${Math.floor(lunch.minutes / 60)}:${String(lunch.minutes % 60).padStart(2, "0")}` : ""}
          </span>
        </td>
        <td className="day-view__project-col" colSpan={2}>
          <span className="day-view__lunch-label">
            <span className="day-view__lunch-name">{taken ? "Lunch" : "No lunch"}</span>
            <span className="day-view__lunch-hint">
              {taken ? "not worked" : "type a start to add one"}
              {isCustom && ` · ${usualText}`}
            </span>
          </span>
          {clash && (
            <span className="day-view__lunch-clash">
              <span>
                {displayTime(clash.entry.start_time)}–{displayTime(clash.entry.end_time)}{" "}
                {clash.endsInside
                  ? `runs ${clash.minutes} min into lunch`
                  : `starts ${clash.minutes} min before lunch ends`}
                , which is not booked.
              </span>
              {!isReadOnly && (
                <button
                  type="button"
                  className="day-view__lunch-fix"
                  onClick={() => commit("start", clash.suggestion)}
                >
                  Take lunch {range(clash.suggestion)}
                </button>
              )}
            </span>
          )}
        </td>
        {!isReadOnly && (
          <td className="ui-table__actions">
            <div className="row-actions">
              {isCustom && (
                <IconButton
                  icon={<ResetIcon size={14} />}
                  label={usual.minutes > 0 ? `Back to the usual ${range(usual)}` : "Back to the usual (no lunch)"}
                  size="sm"
                  onClick={() => {
                    setRefusal(null);
                    onChange("reset", null);
                  }}
                />
              )}
              {taken && (
                <IconButton
                  icon={<CloseIcon size={14} />}
                  label="No lunch today"
                  size="sm"
                  onClick={() => commit("none", { start, minutes: 0 })}
                />
              )}
            </div>
          </td>
        )}
      </tr>
      {refusal && (
        <tr className="day-view__row-error">
          <td colSpan={columns}>
            <p className="field__error" role="alert">
              {refusal}
            </p>
          </td>
        </tr>
      )}
    </>
  );
};

export default DayLunchRow;
